import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Visibility,
  VisibilityOff,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from "@mui/icons-material";

export type ExpirationOption = "1day" | "7days" | "30days" | "never" | "custom";

export interface ExistingShare {
  id: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  itemDescription: string;
  createShare: (
    password: string,
    expiresAt: string | null
  ) => Promise<{ shareId: string }>;
  listShares: () => Promise<ExistingShare[]>;
  deleteShare: (shareId: string) => Promise<void>;
  buildShareUrl: (shareId: string) => string;
  /** Additional content to render after expiration select (e.g., live/snapshot toggle) */
  additionalOptions?: React.ReactNode;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  title,
  itemDescription,
  createShare,
  listShares,
  deleteShare,
  buildShareUrl,
  additionalOptions,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [expiration, setExpiration] = useState<ExpirationOption>("1day");
  const [customExpiration, setCustomExpiration] = useState("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [existingShares, setExistingShares] = useState<ExistingShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);

  const fetchExistingShares = useCallback(async () => {
    setLoadingShares(true);
    try {
      const shares = await listShares();
      setExistingShares(shares);
    } catch (err) {
      console.error("Failed to fetch existing shares:", err);
    } finally {
      setLoadingShares(false);
    }
  }, [listShares]);

  // Fetch existing shares when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingShares();
    }
  }, [open, fetchExistingShares]);

  const handleClose = () => {
    setPassword("");
    setShowPassword(false);
    setExpiration("1day");
    setCustomExpiration("");
    setGeneratedLink(null);
    setError(null);
    setCopied(false);
    setExistingShares([]);
    setCopiedShareId(null);
    onClose();
  };

  const handleDeleteShare = async (shareId: string) => {
    try {
      await deleteShare(shareId);
      setExistingShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch (err) {
      console.error("Failed to delete share:", err);
    }
  };

  const handleCopyExistingShare = async (shareId: string) => {
    const shareUrl = buildShareUrl(shareId);
    await navigator.clipboard.writeText(shareUrl);
    setCopiedShareId(shareId);
    setTimeout(() => setCopiedShareId(null), 2000);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getExpirationDate = (): string | null => {
    const now = new Date();
    switch (expiration) {
      case "1day":
        return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
      case "7days":
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      case "30days":
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
      case "custom":
        return customExpiration ? new Date(customExpiration).toISOString() : "";
      case "never":
        return null;
    }
  };

  const handleGenerate = async () => {
    if (!password) {
      setError("Password is required");
      return;
    }

    if (password.length < 4) {
      setError("Password must be at least 4 characters");
      return;
    }
    if (expiration === "custom" && !customExpiration.trim()) {
      setError("Custom expiration date is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createShare(password, getExpirationDate());
      const shareUrl = buildShareUrl(result.shareId);
      setGeneratedLink(shareUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {!generatedLink ? (
          <>
            {/* Existing Shares Section */}
            {loadingShares ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : existingShares.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <LinkIcon fontSize="small" />
                  Existing Share Links ({existingShares.length})
                </Typography>
                <List dense sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
                  {existingShares.map((share) => {
                    const expired = isExpired(share.expiresAt);
                    return (
                      <ListItem key={share.id}>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                ...{share.id.slice(-8)}
                              </Typography>
                              {expired ? (
                                <Chip
                                  label="Expired"
                                  size="small"
                                  color="error"
                                  sx={{ height: 20, fontSize: "10px" }}
                                />
                              ) : share.expiresAt ? (
                                <Chip
                                  label={`Expires ${formatDate(share.expiresAt)}`}
                                  size="small"
                                  color="warning"
                                  sx={{ height: 20, fontSize: "10px" }}
                                />
                              ) : (
                                <Chip
                                  label="Never expires"
                                  size="small"
                                  color="success"
                                  sx={{ height: 20, fontSize: "10px" }}
                                />
                              )}
                            </Box>
                          }
                          secondary={`Created ${formatDate(share.createdAt)}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleCopyExistingShare(share.id)}
                            disabled={expired}
                            color={
                              copiedShareId === share.id ? "success" : "default"
                            }
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeleteShare(share.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
                <Divider sx={{ my: 2 }} />
              </Box>
            ) : null}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a password-protected link to share {itemDescription}. They
              will need the password to view the details.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="This password will be required to view the shared content"
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={expiration}
                label="Link Expiration"
                onChange={(e) =>
                  setExpiration(e.target.value as ExpirationOption)
                }
              >
                <MenuItem value="1day">1 Day (Recommended)</MenuItem>
                <MenuItem value="7days">7 Days</MenuItem>
                <MenuItem value="30days">30 Days</MenuItem>
                <MenuItem value="custom">Custom Date/Time</MenuItem>
                <MenuItem value="never">Never</MenuItem>
              </Select>
            </FormControl>
            {expiration === "custom" && (
              <TextField
                fullWidth
                label="Custom Expiration"
                type="datetime-local"
                value={customExpiration}
                onChange={(e) => setCustomExpiration(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            )}

            {additionalOptions}
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              Share link generated successfully!
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Copy the link below and share it with the recipient. They will
              need the password you set to view the details.
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <TextField
                fullWidth
                value={generatedLink}
                InputProps={{
                  readOnly: true,
                  sx: { fontSize: "14px" },
                }}
                size="small"
              />
              <IconButton
                onClick={handleCopy}
                color={copied ? "success" : "default"}
              >
                <CopyIcon />
              </IconButton>
            </Box>

            {copied && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ mt: 1, display: "block" }}
              >
                Link copied to clipboard!
              </Typography>
            )}

            <Alert severity="warning" sx={{ mt: 3 }}>
              Remember to share the password separately for security.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {generatedLink ? "Close" : "Cancel"}
        </Button>
        {!generatedLink && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !password}
          >
            {loading ? <CircularProgress size={24} /> : "Generate Link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;
